import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const DEFAULT_FAQS = [
  {
    q: "What does app marketing actually include?",
    a: "App marketing covers everything from App Store Optimization (ASO) and paid user acquisition to influencer campaigns, content marketing, creative production, and international expansion. It's the full funnel — making your app discoverable, compelling, and downloaded at scale.",
  },
  {
    q: "How soon can we expect growth after starting?",
    a: "Most clients see measurable keyword ranking improvements within 4–8 weeks. Meaningful download growth from organic channels typically follows within 3–6 months of consistent execution. Paid and influencer campaigns can drive installs from week one.",
  },
  {
    q: "Do you work with apps across all categories?",
    a: "Yes. We specialise in 50+ categories — gaming, fintech, edtech, health & fitness, dating, food delivery, travel, social, productivity, meditation, e-commerce, and more. Each category gets a tailored strategy, not a one-size-fits-all template.",
  },
  {
    q: "Can AppFillip handle both iOS and Android marketing?",
    a: "Absolutely. Every campaign is built for both the Apple App Store and Google Play Store with platform-specific keyword strategies, creatives, and store listing best practices for each ecosystem.",
  },
  {
    q: "What markets do you operate in?",
    a: "We run campaigns across 15+ markets including the USA, UK, Australia, UAE, Canada, Germany, Japan, Singapore, India, and more. Our international expansion service localises your app listing, creatives, and paid strategy for each target market.",
  },
  {
    q: "Is there a minimum contract length?",
    a: "No lock-in — you can cancel anytime. That said, most clients stay 12+ months because compounding organic growth takes time and the numbers keep improving. We offer month-to-month, 3-month, and 6-month packages.",
  },
];

export default function FAQSection({
  faqs = DEFAULT_FAQS,
  title = "App Marketing — Questions Answered",
  subtitle = "Still curious? Drop a question in the audit form — a real human (not a bot) will reply within 24h.",
  label = "FAQ",
}) {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      className="py-20 md:py-28 bg-white border-t border-zinc-200"
      itemScope
      itemType="https://schema.org/FAQPage"
      data-testid="faq-section"
    >
      <div className="container grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="text-overline text-electric mb-3">{label}</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
            {title}
          </h2>
          <p className="mt-5 text-zinc-600 leading-relaxed">{subtitle}</p>
        </div>

        <ul className="lg:col-span-8 border-t border-zinc-200">
          {faqs.map((f, idx) => {
            const isOpen = open === idx;
            return (
              <li
                key={f.q}
                className="border-b border-zinc-200"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  className="w-full py-6 flex items-center justify-between gap-6 text-left"
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span
                    className="font-display text-lg md:text-xl font-bold text-charcoal"
                    itemProp="name"
                  >
                    {f.q}
                  </span>
                  <span className="w-9 h-9 grid place-items-center border border-charcoal flex-shrink-0">
                    {isOpen ? (
                      <Minus className="w-4 h-4" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </span>
                </button>
                {isOpen && (
                  <div
                    className="pb-6 text-zinc-600 leading-relaxed"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{f.a}</p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
